import React, { useMemo, useState } from 'react';
import { Search, ExternalLink } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth, type NexoraUser } from '../../context/AuthContext';

type Role = NexoraUser['role'];

const ALL_ROLES: Role[] = [
  'SUPER_ADMIN', 'SOCIETY_ADMIN', 'STAFF', 'GUARD', 'TREASURER',
  'VIEW_ONLY_ADMIN', 'THEME_MANAGER', 'ANALYST', 'VIEWER', 'RESIDENT', 'TENANT',
];

const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: 'Super Admin',
  SOCIETY_ADMIN: 'Society Admin',
  STAFF: 'Staff',
  GUARD: 'Guard',
  TREASURER: 'Treasurer',
  VIEW_ONLY_ADMIN: 'View-Only Admin',
  THEME_MANAGER: 'Theme Manager',
  ANALYST: 'Analyst',
  VIEWER: 'Viewer',
  RESIDENT: 'Resident',
  TENANT: 'Tenant',
};

interface RoleGuide {
  role: Role;
  summary: string;
  details: string;
}

const roleGuides: RoleGuide[] = [
  {
    role: 'SUPER_ADMIN',
    summary: 'Platform owner across all societies.',
    details: 'Manages society onboarding, admin invites, and access requests from the platform Admin Panel (/admin). Not tied to a single society.',
  },
  {
    role: 'SOCIETY_ADMIN',
    summary: 'Full control of one society.',
    details: 'Owns every module for their society: community setup, finance, analytics, settings, and team management. Can create Team Members with any of the delegated roles below.',
  },
  {
    role: 'STAFF',
    summary: 'General operational delegate.',
    details: 'A Team Member role assigned by the Society Admin. Handles day-to-day operations — helpdesk, maintenance, assets, facilities, billing — but cannot manage Team Members, Payment Config, or Analytics/Audit Logs.',
  },
  {
    role: 'GUARD',
    summary: 'Security desk / gate operations.',
    details: 'Team Member role focused on gate activity: visitor approvals, delivery logging, and vehicle/parking checks. Mirrors the Guard Gate screen on mobile.',
  },
  {
    role: 'TREASURER',
    summary: 'Finance-focused delegate.',
    details: 'Team Member role with access to financial records such as Documents (statements, receipts) alongside standard resident-facing modules.',
  },
  {
    role: 'VIEW_ONLY_ADMIN',
    summary: 'Read-only oversight.',
    details: 'Team Member role that can view admin modules without making changes — useful for auditors or advisors.',
  },
  {
    role: 'THEME_MANAGER',
    summary: 'Look-and-feel customization.',
    details: 'Team Member role that manages the Premium Themes module (Executive Suite, Modern Minimal, Corporate Edge).',
  },
  {
    role: 'ANALYST',
    summary: 'Reporting and insights.',
    details: 'Team Member role focused on the Analytics & Reporting section and AI Insights.',
  },
  {
    role: 'VIEWER',
    summary: 'Generic read-only access.',
    details: 'Team Member role for stakeholders who only need visibility into society activity.',
  },
  {
    role: 'RESIDENT',
    summary: 'Unit owner / occupant.',
    details: 'Self-service access: pay dues (My Billing), manage their own visitors/deliveries/vehicles/parking, book facilities, raise helpdesk tickets, and message the admin. Available on web and mobile.',
  },
  {
    role: 'TENANT',
    summary: 'Renter, same permissions as Resident.',
    details: 'Treated identically to Resident throughout Nexora — the distinction only matters for records like move-in/move-out and lease-linked billing.',
  },
];

interface DocFeature {
  name: string;
  path?: string;
  description: string;
  roles: Role[];
}

interface DocSection {
  title: string;
  features: DocFeature[];
}

const admin: Role[] = ['SUPER_ADMIN', 'SOCIETY_ADMIN', 'STAFF'];
const adminOnly: Role[] = ['SUPER_ADMIN', 'SOCIETY_ADMIN'];
const everyone: Role[] = ['SUPER_ADMIN', 'SOCIETY_ADMIN', 'STAFF', 'RESIDENT', 'TENANT'];
// Gate-duty delegate — backend grants GUARD read/write access to these endpoints
// (SecurityConfig) even though the dedicated UI is the mobile Guard Gate screen.
const gateAccess: Role[] = ['GUARD'];
// Finance delegate — backend grants TREASURER access to these endpoints (SecurityConfig).
const financeAccess: Role[] = ['TREASURER'];
// Read-only oversight roles — broad GET access to admin modules not otherwise restricted.
const readOnlyOversight: Role[] = ['VIEW_ONLY_ADMIN', 'ANALYST', 'VIEWER'];

const docSections: DocSection[] = [
  {
    title: 'Getting Started',
    features: [
      { name: 'Society Setup', path: '/setup-community', description: 'One-time wizard a new Society Admin completes to configure the society before the dashboard unlocks.', roles: ['SOCIETY_ADMIN'] },
      { name: 'Super Admin Panel', path: '/admin', description: 'Platform-level console for onboarding societies, approving admin invites, and reviewing access requests.', roles: ['SUPER_ADMIN'] },
      { name: 'Dashboard', path: '/dashboard', description: 'Landing overview with key stats, quick actions, and alerts tailored to your role.', roles: [...everyone, ...gateAccess, ...financeAccess, ...readOnlyOversight, 'THEME_MANAGER'] },
    ],
  },
  {
    title: 'Community',
    features: [
      { name: 'Blocks', path: '/dashboard/blocks', description: 'Define the buildings/towers/wings that make up the society.', roles: admin },
      { name: 'Units', path: '/dashboard/units', description: 'Manage individual flats/units and link them to blocks and residents.', roles: admin },
      { name: 'Flat Types', path: '/dashboard/unit-types', description: 'Configure reusable unit templates (e.g. 2BHK, 3BHK) with area and default pricing.', roles: admin },
      { name: 'Residents', path: '/dashboard/residents', description: 'Resident directory: profiles, contact details, and unit assignments.', roles: admin },
      { name: 'Move Requests', path: '/dashboard/move-requests', description: 'Move-in / move-out workflow, including move-out date tracking.', roles: everyone },
      { name: 'Parking', path: '/dashboard/parking', description: 'Allocate and track parking slots per unit or vehicle.', roles: everyone },
      { name: 'Helpers', path: '/dashboard/helpers', description: 'Register domestic help/staff associated with a unit and track their gate entries.', roles: [...admin, ...gateAccess] },
      { name: 'Blacklist', path: '/dashboard/blacklist', description: 'Maintain a list of individuals denied entry at the gate.', roles: [...admin, ...gateAccess] },
      { name: 'Visitors', path: '/dashboard/visitors', description: 'Approve visitor requests and log gate entries via OTP/QR.', roles: [...everyone, ...gateAccess] },
      { name: 'Deliveries', path: '/dashboard/deliveries', description: 'Track packages logged at the gate through to pickup.', roles: everyone },
      { name: 'Facilities', path: '/dashboard/facilities', description: 'Define bookable amenities such as the clubhouse or pool, including rules and capacity.', roles: admin },
      { name: 'Book a Facility', path: '/dashboard/facility-booking', description: 'Reserve a facility slot and view upcoming bookings.', roles: everyone },
      { name: 'Vehicles', path: '/dashboard/vehicles', description: 'Register and manage vehicles linked to a resident or unit.', roles: [...everyone, ...gateAccess] },
      { name: 'Violations', path: '/dashboard/violations', description: 'Log and track society rule violations, with resolution status.', roles: everyone },
      { name: 'Committee Meetings', path: '/dashboard/committee-meetings', description: 'Schedule meetings, share video links, and publish minutes.', roles: everyone },
      { name: 'Messages', path: '/dashboard/messages', description: 'Direct message thread between a resident and the society admin.', roles: ['RESIDENT', 'TENANT'] },
      { name: 'Admin Messages', path: '/dashboard/admin-messages', description: 'Inbox for admins to reply to resident message threads.', roles: adminOnly },
      { name: 'Marketplace', path: '/dashboard/marketplace', description: 'Community classifieds for buying, selling, or offering services.', roles: [...everyone, ...gateAccess] },
      { name: 'Deposits', path: '/dashboard/deposits', description: 'Track security deposits collected from residents.', roles: [...admin, ...financeAccess] },
      { name: 'Guard Gate', description: 'Dedicated gate console (mobile app) for logging visitors, deliveries, and vehicle checks.', roles: gateAccess },
    ],
  },
  {
    title: 'Finance',
    features: [
      { name: 'Billing', path: '/dashboard/billing', description: 'Generate and manage resident maintenance invoices.', roles: [...admin, ...financeAccess] },
      { name: 'My Billing', path: '/dashboard/my-billing', description: 'View and pay your own dues, with a real-time outstanding balance banner.', roles: ['RESIDENT', 'TENANT'] },
      { name: 'Expenses', path: '/dashboard/expenses', description: 'Record and categorize society operating expenses.', roles: [...admin, ...financeAccess] },
      { name: 'Ledger', path: '/dashboard/ledger', description: 'A running general ledger of all financial transactions.', roles: [...admin, ...financeAccess] },
      { name: 'Budgets', path: '/dashboard/budgets', description: 'Plan and track annual or category-wise budgets.', roles: [...admin, ...financeAccess] },
      { name: 'Funds', path: '/dashboard/funds', description: 'Track sinking funds and reserve funds.', roles: [...admin, ...financeAccess] },
      { name: 'Monthly Charges', path: '/dashboard/monthly-charges', description: 'Configure automatic recurring maintenance billing with sequential invoice numbers and reminders.', roles: [...admin, ...financeAccess] },
      { name: 'Accounting', path: '/dashboard/accounting', description: 'Consolidated financial reporting across billing, expenses, and ledger.', roles: [...admin, ...financeAccess] },
    ],
  },
  {
    title: 'Analytics & Reporting',
    features: [
      { name: 'Executive Dashboard', path: '/dashboard/analytics', description: 'High-level KPIs and trends across the society.', roles: [...adminOnly, 'ANALYST', 'VIEW_ONLY_ADMIN', 'VIEWER'] },
      { name: 'Reports', path: '/dashboard/reports', description: 'Generate and export detailed operational and financial reports.', roles: [...adminOnly, 'ANALYST', 'VIEW_ONLY_ADMIN', 'VIEWER'] },
      { name: 'Audit Logs', path: '/dashboard/audit-logs', description: 'Immutable log of admin actions for accountability and compliance.', roles: [...adminOnly, 'ANALYST', 'VIEW_ONLY_ADMIN', 'VIEWER'] },
    ],
  },
  {
    title: 'Operations',
    features: [
      { name: 'Helpdesk', path: '/dashboard/helpdesk', description: 'Raise and track support tickets for maintenance or general issues.', roles: everyone },
      { name: 'Maintenance', path: '/dashboard/maintenance', description: 'Manage maintenance work orders assigned to staff or vendors.', roles: admin },
      { name: 'Assets & Vendors', path: '/dashboard/assets', description: 'Track society assets and the vendors who service them.', roles: admin },
      { name: 'Staff & Payroll', path: '/dashboard/staff', description: 'Manage society staff records and process payroll.', roles: [...admin, ...gateAccess] },
      { name: 'Noticeboard', path: '/dashboard/notices', description: 'Publish notices to the community; residents can be granted posting rights.', roles: everyone },
      { name: 'Polls', path: '/dashboard/polls', description: 'Create polls to gather resident opinions and votes.', roles: everyone },
      { name: 'Events', path: '/dashboard/events', description: 'Announce and RSVP to community events.', roles: everyone },
      { name: 'Security', path: '/dashboard/security', description: 'Security overview: gate activity, incidents, and guard shift status.', roles: everyone },
      { name: 'Documents', path: '/dashboard/documents', description: 'Shared document repository (bylaws, circulars, financial statements).', roles: [...everyone, 'TREASURER'] },
      { name: 'Community Feed', path: '/dashboard/community-feed', description: 'A social feed of community updates and resident posts.', roles: everyone },
    ],
  },
  {
    title: 'Settings',
    features: [
      { name: 'Team Members', path: '/dashboard/team-members', description: 'Invite and manage delegated admin roles (Staff, Guard, Treasurer, Analyst, etc.).', roles: adminOnly },
      { name: 'Community Features', path: '/dashboard/community-features', description: 'Toggle which community features (posting, events, etc.) residents can use.', roles: admin },
      { name: 'Payment Config', path: '/dashboard/payment-config', description: 'Configure payment gateway and billing settings for the society.', roles: [...adminOnly, ...financeAccess] },
      { name: 'Settings', path: '/dashboard/settings', description: 'Personal account settings, including password change.', roles: everyone },
    ],
  },
  {
    title: 'Premium Themes',
    features: [
      { name: 'Executive Suite / Modern Minimal / Corporate Edge', path: '/dashboard/themes/executive-suite', description: 'Preview and apply premium visual themes for the dashboard.', roles: [...admin, 'THEME_MANAGER'] },
    ],
  },
  {
    title: 'AI Features',
    features: [
      { name: 'NexaBot', path: '/dashboard/nexabot', description: 'AI chat assistant for quick answers and in-app actions.', roles: admin },
      { name: 'AI Insights', path: '/dashboard/insights', description: 'AI-generated insights and recommendations from society data.', roles: [...admin, 'ANALYST'] },
    ],
  },
];

const roleBadgeStyle = (active: boolean): React.CSSProperties => ({
  padding: '5px 11px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  border: '1px solid var(--nx-border)',
  background: active ? 'var(--nx-primary-accent)' : 'var(--nx-surface-3)',
  color: active ? '#fff' : 'var(--nx-text-secondary)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const DocsPage: React.FC = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | 'ALL'>('ALL');

  const filteredSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docSections
      .map(section => ({
        ...section,
        features: section.features.filter(f => {
          const matchesRole = roleFilter === 'ALL' || f.roles.includes(roleFilter);
          const matchesSearch = q === '' || f.name.toLowerCase().includes(q) || f.description.toLowerCase().includes(q);
          return matchesRole && matchesSearch;
        }),
      }))
      .filter(section => section.features.length > 0);
  }, [search, roleFilter]);

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 6 }}>
          Nexora Documentation
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          A complete guide to every role and feature in Nexora, for web and mobile.
        </p>
      </div>

      {/* Role guide */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 12 }}>
          Roles in Nexora
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {roleGuides.map(g => (
            <div key={g.role} style={{
              background: 'var(--nx-surface-2)',
              border: g.role === user?.role ? '1px solid var(--nx-primary-accent)' : '1px solid var(--nx-border)',
              borderRadius: 10,
              padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text-primary)', fontFamily: 'Syne, sans-serif' }}>
                  {ROLE_LABEL[g.role]}
                </span>
                {g.role === user?.role && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-primary-accent)' }}>YOU</span>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'var(--nx-text-secondary)', fontWeight: 600, marginBottom: 4 }}>{g.summary}</p>
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>{g.details}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button style={roleBadgeStyle(roleFilter === 'ALL')} onClick={() => setRoleFilter('ALL')}>All roles</button>
          {ALL_ROLES.map(r => (
            <button key={r} style={roleBadgeStyle(roleFilter === r)} onClick={() => setRoleFilter(r)}>
              {ROLE_LABEL[r]}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)' }} />
          <input
            type="text"
            placeholder="Search features…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: '7px 12px 7px 30px',
              borderRadius: 8,
              border: '1px solid var(--nx-border)',
              background: 'var(--nx-surface-2)',
              color: 'var(--nx-text-primary)',
              fontSize: 12,
              outline: 'none',
              width: 220,
              fontFamily: 'var(--font-body)',
            }}
          />
        </div>
      </div>

      {/* Feature catalog */}
      {filteredSections.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>
          No features match your filters.
        </div>
      ) : (
        filteredSections.map(section => (
          <div key={section.title} style={{ marginBottom: 26 }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 10 }}>
              {section.title}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {section.features.map(f => (
                <div key={f.name} style={{
                  border: '1px solid var(--nx-border)',
                  borderRadius: 10,
                  padding: '12px 16px',
                  background: 'var(--nx-surface-2)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      {f.path ? (
                        <a href={f.path} style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          {f.name} <ExternalLink size={11} style={{ opacity: 0.5 }} />
                        </a>
                      ) : (
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)' }}>{f.name}</span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5, marginBottom: 6 }}>{f.description}</p>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {f.roles.map(r => (
                        <span key={r} style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: 'var(--nx-surface-3)',
                          color: 'var(--nx-text-secondary)',
                        }}>
                          {ROLE_LABEL[r]}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Mobile note */}
      <div style={{
        marginTop: 8,
        padding: '14px 18px',
        borderRadius: 10,
        background: 'rgba(26,108,246,0.06)',
        border: '1px solid rgba(26,108,246,0.2)',
        fontSize: 12,
        color: 'var(--nx-text-secondary)',
        lineHeight: 1.6,
      }}>
        <strong style={{ color: 'var(--nx-text-primary)' }}>Mobile app:</strong> Nexora's mobile app mirrors these features for Residents, Guards, and Admins, plus dedicated screens for approvals, admin society management, and admin invites. Open the <strong>Help &amp; Support → Docs</strong> tab in the mobile app for the same guide on the go.
      </div>
    </PageLayout>
  );
};

export default DocsPage;
